// src/components/BookMetadataEditor.tsx

import React, { useContext } from 'react'; // Added useContext
import { BookMetadata } from '../types';
import { labelClass, textareaClass, inputClass, primaryButtonClass } from '../constants';
import { AppContext, AppContextType } from '../contexts/AppContext'; // Import AppContext
import { 
    useViewEditMode, 
    ViewEditSection, 
    SaveCancelButtons, 
    DisplayField, 
    DisplayGrid 
} from '../utils/viewEditUtils';
import { useOptimizedSave } from '../hooks/useOptimizedSave';

interface BookMetadataEditorProps {
    bookMetadata: BookMetadata;
    onBookMetadataChange: (field: keyof BookMetadata, value: string) => void;
    hasInitialBlueprint: boolean; // Added prop
}

const metadataFields: Array<{ key: keyof BookMetadata; label: string; placeholder: string, type?: 'input' | 'textarea' | 'number' }> = [
    { key: 'subtitle', label: '1. Book Subtitle', placeholder: 'e.g., A thrilling adventure, A guide to success', type: 'input' },
    { key: 'blurb', label: '2. Book Description/Blurb', placeholder: 'Brief description of your book for back cover and marketing materials. Keep it engaging and concise.', type: 'textarea' },
    { key: 'fictionVsNonFiction', label: '3. Fiction vs Non-Fiction', placeholder: 'e.g., Fiction, Non-fiction, Historical Fiction', type: 'input' },
    { key: 'settingTime', label: '4. Setting & Time', placeholder: 'e.g., Contemporary, Futuristic, Victorian Era', type: 'input' },
    { key: 'audienceAgeBand', label: '5. Audience / Age Band', placeholder: 'e.g., Young Adult, Adult Trade, Middle Grade', type: 'input' },
    { key: 'narrativePointOfView', label: '6. Narrative Point-of-View', placeholder: 'e.g., 1st-person singular, 3rd-person limited', type: 'input' },
    { key: 'tense', label: '7. Tense', placeholder: 'e.g., Past, Present', type: 'input' },
    { key: 'voiceStyle', label: '8. Voice & Style', placeholder: 'e.g., Formal, Colloquial, Comic, Lyrical', type: 'textarea' },
    { key: 'structure', label: '9. Structure', placeholder: 'e.g., Linear chapters, Three-act structure, Non-linear', type: 'textarea' },
    // themesMotifs removed - themes now managed in World Building section
    { key: 'purposeFunction', label: '10. Purpose / Function', placeholder: 'e.g., Entertain, Inform, Inspire, Satirise', type: 'textarea' },
    { key: 'mediumFormat', label: '11. Medium & Format', placeholder: 'e.g., Prose (print/e-book), Graphic novel, Audio-first', type: 'input' },
    { key: 'paratextualElements', label: '12. Paratextual Elements', placeholder: 'e.g., Preface, Maps, Glossary, Appendices, None planned', type: 'textarea' },
    { key: 'scholarlyApparatus', label: '13. Scholarly Apparatus', placeholder: 'For academic: e.g., Abstract, Citations, Methodology, Not applicable', type: 'textarea' },
    { key: 'legalMarketTags', label: '14. Legal & Market Tags', placeholder: 'e.g., Standard Copyright, ISBN (later), BISAC codes (later)', type: 'textarea' },
];

const BookMetadataEditor: React.FC<BookMetadataEditorProps> = ({ bookMetadata, onBookMetadataChange, hasInitialBlueprint }) => {
    const appContext = useContext(AppContext);
    const { activeEditSection, handleEditToggle, handleSaveAndExit, handleCancelEdit } = useViewEditMode();
    const { saveBookMetadata } = useOptimizedSave();

    // Get blueprint state management functions from optimized data context
    const { blueprint } = appContext?.optimizedData || {};

    const handleSaveChanges = async () => {

        try {
            await saveBookMetadata.mutateAsync({
                projectId: appContext?.dbProject?.uuid || appContext?.dbProject?.id?.toString() || '',
                bookMetadata,
                setBlueprintSaving: blueprint?.setBlueprintSaving,
                onStateUpdate: (updatedData: any) => {
                    // Update local project state with fresh data from successful save
                    if (appContext?.setProjectState && updatedData.bookMetadata) {
                        appContext.setProjectState(prevState => {
                            if (!prevState) return prevState;
                            return {
                                ...prevState,
                                bookMetadata: updatedData.bookMetadata,
                                updatedAt: updatedData.updatedAt || new Date().toISOString(),
                                isUnsaved: false, // Mark as saved
                            };
                        });
                    }
                }
            });
            handleSaveAndExit();
        } catch (error) {
            console.error('Failed to save book metadata:', error);
        }
    };
    
    // BLUEPRINT MIGRATION: Use blueprint snapshot instead of separate worldBuildingData snapshot
    const showPotentialRevisionNeededNote = appContext?.projectState && 
                                           appContext.projectState.chapters?.length > 0 && 
                                           (appContext.projectState.bookMetadataSnapshotForChapters !== null || 
                                            appContext.projectState.blueprintSnapshotForChapters !== null);

    // Group fields for better organization
    const basicFields = metadataFields.slice(0, 6); // Subtitle, blurb, fiction type, setting, audience, POV
    const narrativeFields = metadataFields.slice(6, 9); // Tense, voice, structure
    const styleFields = metadataFields.slice(9, 13); // Purpose, medium, paratextual, scholarly
    const additionalFields = metadataFields.slice(13); // Legal market tags

    const renderEditForm = (fields: typeof metadataFields) => (
        <div className="space-y-4">
            {fields.map(({ key, label, placeholder, type = 'textarea' }) => (
                <div key={String(key)}>
                    <label htmlFor={String(key)} className={`${labelClass} mb-1.5`}>{label}:</label>
                    {type === 'textarea' ? (
                         <textarea
                            id={String(key)}
                            value={String(bookMetadata[key as keyof BookMetadata] || '')}
                            onChange={(e) => onBookMetadataChange(key as string & keyof BookMetadata, e.target.value)}
                            placeholder={placeholder}
                            className={`${textareaClass} min-h-[60px]`}
                            rows={2}
                            disabled={!hasInitialBlueprint}
                        />
                    ) : (
                        <input
                            id={String(key)}
                            type={type === 'number' ? 'number' : 'text'}
                            value={String(bookMetadata[key as keyof BookMetadata] || '')}
                            onChange={(e) => onBookMetadataChange(key as string & keyof BookMetadata, e.target.value)}
                            placeholder={placeholder}
                            className={inputClass}
                            min={type === 'number' ? "0" : undefined}
                            disabled={!hasInitialBlueprint}
                        />
                    )}
                </div>
            ))}
        </div>
    );

    const renderDisplayView = (fields: typeof metadataFields) => (
        <DisplayGrid>
            {fields.map(({ key, label }) => (
                <DisplayField
                    key={String(key)}
                    label={label}
                    value={bookMetadata[key as keyof BookMetadata]}
                    background={true}
                />
            ))}
        </DisplayGrid>
    );

    return (
        <div className="space-y-6 p-1 h-full overflow-y-auto">
            <h2 className="text-2xl font-semibold text-gray-800 border-b pb-3 mb-6">Overall Book Details</h2>

            <div className="p-3 mb-4 text-sm text-blue-700 bg-blue-100 rounded-lg" role="note">
                <strong>Note:</strong> This section now includes subtitle and book description fields for cover generation. Genre information is managed in the <em>Publishing Essentials</em> section, and themes & motifs are managed in the <em>World Building</em> section for better organization and metadata handling.
            </div>

            {!hasInitialBlueprint && (
                <div className="p-3 mb-4 text-sm text-yellow-700 bg-yellow-100 rounded-lg" role="alert">
                    Generate your initial blueprint first in the 'Idea Hub' to populate these details.
                </div>
            )}

            {showPotentialRevisionNeededNote && (
                <div className="p-3 mb-4 text-xs text-blue-700 bg-blue-100 rounded-lg dark:bg-blue-200 dark:text-blue-800" role="note">
                    Note: Chapter outlines currently exist. Changes made here may require you to revise these outlines later in the 'Chapters Building' section to ensure consistency.
                </div>
            )}
            
            {/* Basic Information Section */}
            <ViewEditSection
                title="Basic Information"
                subtitle="Essential book details including subtitle, description, and target audience"
                isActive={activeEditSection === 'basic'}
                onEditToggle={() => handleEditToggle('basic')}
            >
                {activeEditSection === 'basic' ? (
                    <>
                        {renderEditForm(basicFields)}
                        <SaveCancelButtons
                            onSave={handleSaveChanges}
                            onCancel={handleCancelEdit}
                        />
                    </>
                ) : (
                    renderDisplayView(basicFields)
                )}
            </ViewEditSection>

            {/* Narrative Structure Section */}
            <ViewEditSection
                title="Narrative Structure"
                subtitle="Setting, audience, and narrative approach"
                isActive={activeEditSection === 'narrative'}
                onEditToggle={() => handleEditToggle('narrative')}
            >
                {activeEditSection === 'narrative' ? (
                    <>
                        {renderEditForm(narrativeFields)}
                        <SaveCancelButtons
                            onSave={handleSaveChanges}
                            onCancel={handleCancelEdit}
                        />
                    </>
                ) : (
                    renderDisplayView(narrativeFields)
                )}
            </ViewEditSection>

            {/* Style & Format Section */}
            <ViewEditSection
                title="Style & Format"
                subtitle="Writing style, themes, and structure"
                isActive={activeEditSection === 'style'}
                onEditToggle={() => handleEditToggle('style')}
            >
                {activeEditSection === 'style' ? (
                    <>
                        {renderEditForm(styleFields)}
                        <SaveCancelButtons
                            onSave={handleSaveChanges}
                            onCancel={handleCancelEdit}
                        />
                    </>
                ) : (
                    renderDisplayView(styleFields)
                )}
            </ViewEditSection>

            {/* Additional Details Section */}
            <ViewEditSection
                title="Additional Details"
                subtitle="Paratextual elements and publishing details"
                isActive={activeEditSection === 'additional'}
                onEditToggle={() => handleEditToggle('additional')}
            >
                {activeEditSection === 'additional' ? (
                    <>
                        {renderEditForm(additionalFields)}
                        <SaveCancelButtons
                            onSave={handleSaveChanges}
                            onCancel={handleCancelEdit}
                        />
                    </>
                ) : (
                    renderDisplayView(additionalFields)
                )}
            </ViewEditSection>

             <p className="text-xs text-gray-500 mt-6">
                These details are automatically deduced when an outline is generated. You can refine them here. 
                Remember to "Save Project" to persist your changes.
            </p>
        </div>
    );
};

export default BookMetadataEditor;